import { useMemo, useState, type ReactNode } from 'react'
import { cn } from '@/lib/cn'

/* Rule 14 (Ben 2026-10-15): every table column decides sort and/or filter — text (contains) · pick (dropdown of its
   values) · has (มี / ไม่มี) — or neither. Head ⇅ → ▼ (numbers มาก→น้อย, text ก→ฮ) → ▲ → off; one filter row under
   the heads; an active filter is orange; ✕ ล้างตัวกรอง when any is on. Same look as bbt-branch-order tHead/tRows. */

export type SFCol<T> = {
  k: string
  label: ReactNode
  /** sort key; numbers sort มาก→น้อย first, strings ก→ฮ first */
  sort?: (r: T) => string | number | null | undefined
  filter?: 'text' | 'pick' | 'has'
  /** the text a filter matches (pick lists its distinct values); 'has' = non-empty */
  val?: (r: T) => string
  /** pick options [value, label] when the raw values are not the labels */
  opts?: [string, string][]
  className?: string
}

type State = { key: string; dir: 1 | -1; f: Record<string, string> }

export function useSortFilter<T>(cols: SFCol<T>[], rows: T[], pin?: (r: T) => number) {
  const [s, setS] = useState<State>({ key: '', dir: -1, f: {} })
  const out = useMemo(() => {
    const kept = rows.filter((r) =>
      cols.every((c) => {
        const v = s.f[c.k]
        if (!v || !c.filter) return true
        const x = String(c.val?.(r) ?? '')
        if (c.filter === 'has') return v === 'y' ? x.trim() !== '' : x.trim() === ''
        return c.filter === 'text' ? x.toLowerCase().includes(v.toLowerCase()) : x === v
      }),
    )
    const c = cols.find((x) => x.k === s.key && x.sort)
    // `pin` keeps a group (e.g. switched-off people) at the bottom whatever the column sort says.
    return kept
      .map((r, i) => ({ r, i }))
      .sort((a, b) => {
        const p = pin ? pin(a.r) - pin(b.r) : 0
        if (p) return p
        if (!c) return a.i - b.i
        const x = c.sort!(a.r)
        const y = c.sort!(b.r)
        const d =
          typeof x === 'number' && typeof y === 'number'
            ? x - y
            : String(x ?? '').localeCompare(String(y ?? ''), 'th')
        return s.dir * d || a.i - b.i
      })
      .map((x) => x.r)
  }, [rows, cols, s, pin])

  const firstDir = (c: SFCol<T>): 1 | -1 =>
    typeof rows.map((r) => c.sort?.(r)).find((v) => v != null && v !== '') === 'number' ? -1 : 1
  return {
    rows: out,
    state: s,
    active: Object.values(s.f).some(Boolean),
    sortBy: (k: string) => {
      const c = cols.find((x) => x.k === k)
      if (!c?.sort) return
      const first = firstDir(c)
      setS((p) =>
        p.key !== k ? { ...p, key: k, dir: first } : p.dir === first ? { ...p, dir: (-first as 1 | -1) } : { ...p, key: '', dir: -1 },
      )
    },
    filter: (k: string, v: string) => setS((p) => ({ ...p, f: { ...p.f, [k]: v } })),
    clear: () => setS((p) => ({ ...p, f: {} })),
  }
}

const fBase =
  'h-7 w-full min-w-[4.5rem] rounded border bg-background px-1.5 text-xs font-normal text-foreground transition-colors duration-300 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring'

/** Two header rows: the heads (tap = sort) and the filter row. Render inside <thead>. */
export function SFHead<T>({ cols, rows, sf }: { cols: SFCol<T>[]; rows: T[]; sf: ReturnType<typeof useSortFilter<T>> }) {
  const s = sf.state
  return (
    <>
      <tr className="border-b border-border">
        {cols.map((c) => {
          const on = !!c.sort && s.key === c.k
          return (
            <th
              key={c.k}
              onClick={c.sort ? () => sf.sortBy(c.k) : undefined}
              title={c.sort ? 'แตะเพื่อเรียง' : undefined}
              className={cn(
                'h-9 whitespace-nowrap px-2 text-left align-middle text-xs font-semibold text-muted-foreground',
                c.sort && 'cursor-pointer select-none transition-colors duration-300 hover:text-foreground',
                on && 'text-beijer',
                c.className,
              )}
            >
              {c.label}
              {c.sort && <span className="ml-1 text-[10px] opacity-70">{on ? (s.dir === -1 ? '▼' : '▲') : '⇅'}</span>}
            </th>
          )
        })}
      </tr>
      {cols.some((c) => c.filter) && (
        <tr className="border-b border-border bg-muted/30">
          {cols.map((c) => {
            const v = s.f[c.k] ?? ''
            const tone = v ? 'border-accent-orange bg-accent-orange/10' : 'border-input'
            let el: ReactNode = null
            if (c.filter === 'text')
              el = <input aria-label={`กรอง ${typeof c.label === 'string' ? c.label : c.k}`} placeholder="กรอง" value={v} onChange={(e) => sf.filter(c.k, e.target.value)} className={cn(fBase, tone)} />
            else if (c.filter) {
              const opts: [string, string][] =
                c.filter === 'has'
                  ? [['y', 'มี'], ['n', 'ไม่มี']]
                  : (c.opts ?? [...new Set(rows.map((r) => String(c.val?.(r) ?? '')).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'th')).map((x) => [x, x] as [string, string]))
              el = (
                <select aria-label={`กรอง ${typeof c.label === 'string' ? c.label : c.k}`} value={v} onChange={(e) => sf.filter(c.k, e.target.value)} className={cn(fBase, tone)}>
                  <option value="">ทั้งหมด</option>
                  {opts.map(([ov, ol]) => (
                    <option key={ov} value={ov}>
                      {ol}
                    </option>
                  ))}
                </select>
              )
            }
            return (
              <th key={c.k} className={cn('px-1 py-1 align-middle', c.className)}>
                {el}
              </th>
            )
          })}
        </tr>
      )}
    </>
  )
}
